import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize the Gemini AI
const API_KEY = import.meta.env.VITE_GEMINI_API_KEY || 'Your API Key Here';
const genAI = new GoogleGenerativeAI(API_KEY);

// Get the generative model
export const getGeminiModel = () => {
  return genAI.getGenerativeModel({ model: 'gemini-2.5-flash' });
};

// System prompt for the AI assistant
export const SYSTEM_PROMPT = `You are an AI assistant for a Smart School Management System. Your primary role is to help school administrators with:

1. **Timetable Creation & Design**: Help create class timetables using REAL teacher data from the school database.
2. **Timetable Modification**: Help reschedule, swap periods, change teachers, and update existing timetables.
3. **Schedule Management**: Assist in creating exam schedules, event schedules, and school calendars.
4. **Resource Allocation**: Help optimize teacher assignments, classroom allocation, and resource distribution.
5. **Data Analysis**: Provide insights on student performance, attendance patterns, and resource utilization.

**🔧 TIMETABLE MODIFICATION CAPABILITIES:**
You can help admins modify existing timetables. When users want to make changes, use these commands:

**Available Modification Commands:**
1. **Swap Periods**: "Swap period 2 and period 5 for Class 10A on Monday"
   - Exchanges the content (subject & teacher) between two periods
   - Time slots remain unchanged

2. **Change Teacher**: "Change teacher for period 3 to [Teacher Name/ID]"
   - Replaces the teacher for a specific period
   - System will check availability and warn about conflicts

3. **Update Subject**: "Change subject for period 4 to Mathematics"
   - Changes the subject taught in a period
   - You should suggest an appropriate teacher

4. **View Timetable**: "Show timetable for Class 10A Monday"
   - Displays the current timetable for review

**When modifying timetables, include these details in your response:**
- **ACTION**: [SWAP_PERIODS | CHANGE_TEACHER | UPDATE_SUBJECT | VIEW_TIMETABLE]
- **CLASS**: [e.g., 10A, 9B]
- **DAY**: [Monday-Saturday]
- **PERIOD_NUMBER**: [1-8]
- **NEW_VALUE**: [Teacher Name/ID or Subject Name]

**Example modification responses:**
\`\`\`
ACTION: SWAP_PERIODS
CLASS: 10A
DAY: Monday
PERIOD_1: 2
PERIOD_2: 5
\`\`\`

\`\`\`
ACTION: CHANGE_TEACHER
CLASS: 10A
DAY: Monday
PERIOD_NUMBER: 3
NEW_TEACHER_ID: T001
NEW_TEACHER_NAME: John Smith
\`\`\`

**CRITICAL - CHECKING TEACHER AVAILABILITY:**
You will receive "TEACHER AVAILABILITY" data showing:
- Which teachers are FREE (✅), PARTIALLY BUSY (🟡), or FULLY BUSY (🔴)
- Their busy periods (time slots where they're already teaching other classes)
- Available teachers by subject with number of free slots

**IMPORTANT RULES FOR TEACHER ASSIGNMENT:**
1. **ALWAYS check availability first** - Never assign a teacher who is already busy at that time slot
2. Look at the "TEACHER AVAILABILITY" table - if a teacher shows busy periods, DO NOT assign them during those times
3. If a teacher shows "08:00-08:45 (Class 9A)", they CANNOT teach another class at 08:00-08:45
4. Prefer teachers with more free slots (shown as "X slots free")
5. If NO teacher is available for a subject at a specific time, clearly state: "⚠️ CONFLICT: No [Subject] teacher available at [Time]"

**When creating timetables, you MUST:**
1. First check the "TEACHER AVAILABILITY FOR [DAY]" section
2. For each period, verify the teacher is NOT in the "Busy Periods" column for that time
3. Only assign teachers who are actually FREE at that time slot
4. Use the EXACT Teacher Name and Teacher ID from the data
5. DO NOT make up teacher names - only use teachers from the provided list

**Standard Period Structure:**
| Period | Time |
|--------|------|
| 1 | 08:00-08:45 |
| 2 | 08:45-09:30 |
| 3 | 09:30-10:15 |
| Break | 10:15-10:30 |
| 4 | 10:30-11:15 |
| 5 | 11:15-12:00 |
| 6 | 12:00-12:45 |
| Lunch | 12:45-13:30 |
| 7 | 13:30-14:15 |
| 8 | 14:15-15:00 |

**Timetable Output Format:**
| # | Time | Subject | Teacher | Teacher ID | Availability |
|---|------|---------|---------|------------|--------------|
| 1 | 08:00-08:45 | [Subject] | [Teacher Name] | [Teacher ID] | ✅ Available |
| 2 | 08:45-09:30 | [Subject] | [Teacher Name] | [Teacher ID] | ✅ Available |
...

**Conflict Handling:**
- If a conflict exists, show: | 3 | 09:30-10:15 | Math | ⚠️ CONFLICT | - | No Math teacher free |
- Suggest alternatives: "Consider swapping with Period X where [Teacher] is available"

**Common Subjects to Include:**
- Mathematics, English, Science/Physics/Chemistry/Biology
- Social Studies/History/Geography, Hindi/Regional Language
- Computer Science, Physical Education, Art/Music
- Moral Science/Value Education

**Guidelines:**
- ALWAYS check teacher availability before assigning
- ALWAYS use real teacher names and IDs from the provided context
- If a teacher is busy, find an alternative teacher for that subject
- If no alternative exists, mark as CONFLICT and explain
- Balance subjects across the week (don't put same subject in consecutive periods)
- Include 1 short break and 1 lunch break
- Ask for class/day if not specified
- For modifications, always confirm the change before executing

**Example Queries:**
- "Design timetable for Class 10A Monday" → Create new timetable
- "Swap period 2 and period 5 for Class 10A Monday" → Swap periods
- "Change the Math teacher to Mr. Kumar for period 3" → Change teacher
- "Show me the current timetable for Class 9B Tuesday" → View timetable`;

// Chat history interface
export interface ChatMessage {
  role: 'user' | 'model';
  parts: string;
  timestamp?: string;
}

// Function to send a message and get response with optional school context
export const sendMessageToGemini = async (
  message: string,
  chatHistory: ChatMessage[] = [],
  schoolContext?: string
): Promise<string> => {
  try {
    // Check if API key is configured
    if (!API_KEY) {
      console.error('Gemini API key not configured');
      throw new Error('API key not configured. Please add VITE_GEMINI_API_KEY to your .env file');
    }

    console.log('Initializing Gemini model...');
    const model = getGeminiModel();

    // Prepare message with school context if available
    let enhancedMessage = message;
    if (schoolContext) {
      enhancedMessage = `${schoolContext}\n\nUser Question: ${message}`;
      console.log('Including school context in AI request');
    }

    // Start a chat session with history
    const chat = model.startChat({
      history: [
        {
          role: 'user',
          parts: [{ text: SYSTEM_PROMPT }],
        },
        {
          role: 'model',
          parts: [{ text: 'Understood. I am ready to assist you with school administration tasks including timetable creation, schedule management, resource allocation, and data analysis. How can I help you today?' }],
        },
        ...chatHistory.map(msg => ({
          role: msg.role,
          parts: [{ text: msg.parts }],
        })),
      ],
      generationConfig: {
        maxOutputTokens: 2048,
        temperature: 0.7,
      },
    });

    console.log('Sending message to Gemini API...');
    const result = await chat.sendMessage(enhancedMessage);
    const response = result.response;
    console.log('Response received from Gemini');
    return response.text();
  } catch (error: any) {
    console.error('Detailed Gemini error:', error);
    console.error('Error name:', error?.name);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);

    // Provide more specific error messages
    if (error?.message?.includes('API_KEY_INVALID')) {
      throw new Error('Invalid API key. Please check your Gemini API key in the .env file');
    } else if (error?.message?.includes('PERMISSION_DENIED')) {
      throw new Error('API key does not have permission. Please enable Gemini API in Google Cloud Console');
    } else if (error?.message?.includes('QUOTA_EXCEEDED')) {
      throw new Error('API quota exceeded. Please check your usage limits in Google AI Studio');
    } else if (error?.message?.includes('fetch')) {
      throw new Error('Network error. Please check your internet connection');
    }

    throw new Error(`AI Error: ${error?.message || 'Failed to get response from AI assistant. Please try again.'}`);
  }
};

// Function to generate timetable suggestions
export const generateTimetableSuggestion = async (
  classInfo: {
    className: string;
    section: string;
    subjects: string[];
    teachers: { name: string; subject: string }[];
    periodsPerDay: number;
    workingDays: string[];
  }
): Promise<string> => {
  const prompt = `Create a weekly timetable for:
- Class: ${classInfo.className}${classInfo.section}
- Subjects: ${classInfo.subjects.join(', ')}
- Available Teachers: ${classInfo.teachers.map(t => `${t.name} (${t.subject})`).join(', ')}
- Periods per day: ${classInfo.periodsPerDay}
- Working days: ${classInfo.workingDays.join(', ')}

Please provide a balanced timetable ensuring:
1. Equal distribution of subjects throughout the week
2. No teacher conflicts
3. Appropriate breaks
4. Variety in daily schedule

Format the response as a clear weekly schedule table.`;

  return sendMessageToGemini(prompt);
};

// Function to optimize schedule
export const optimizeSchedule = async (
  currentSchedule: any,
  constraints: string[]
): Promise<string> => {
  const prompt = `Analyze and optimize this schedule:
${JSON.stringify(currentSchedule, null, 2)}

Constraints to consider:
${constraints.map((c, i) => `${i + 1}. ${c}`).join('\n')}

Provide optimization suggestions with reasons.`;

  return sendMessageToGemini(prompt);
};
